import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart3, TrendingUp, Users, Target, Activity,
  ArrowUp, ArrowDown, Minus, RefreshCw,
  Award, DollarSign, Calendar, PieChart
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  PieChart as RechartsPie, Pie, Cell, 
  AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';
import { ChartContainer } from '../components/ChartContainer';
import { formatCurrencyCompact, formatCurrencyFull } from '../utils/formatCurrency';

const COLORS = ['#1e3a5f', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const STATUS_COLORS = {
  draft: '#94a3b8',
  pending_approval: '#f59e0b',
  approved: '#3b82f6',
  sent: '#8b5cf6',
  won: '#10b981',
  lost: '#ef4444'
};

// StatCard component - moved outside to prevent re-creation on each render
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'slate' }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-[#1e3a5f]">{value}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
    </div>
    {trend !== undefined && (
      <div className={`flex items-center gap-1 mt-3 text-sm ${
        trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-500' : 'text-slate-400'
      }`}>
        {trend > 0 ? <ArrowUp className="w-4 h-4" /> : 
         trend < 0 ? <ArrowDown className="w-4 h-4" /> : 
         <Minus className="w-4 h-4" />}
        <span>{Math.abs(trend)}% vs previous period</span>
      </div>
    )}
  </div>
);

export const AnalyticsPage = () => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [period, setPeriod] = useState('30d');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [quotePerformance, setQuotePerformance] = useState({ by_status: [], by_discipline: [] });
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [leadSourceData, setLeadSourceData] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [overviewRes, funnelRes, trendRes, quoteRes, teamRes, leadSourceRes] = await Promise.all([
        authFetch(`/analytics/overview?period=${period}`),
        authFetch(`/analytics/pipeline-funnel?period=${period}`),
        authFetch(`/analytics/revenue-trend?period=${period}`),
        authFetch(`/analytics/quote-performance?period=${period}`),
        authFetch(`/analytics/team-performance?period=${period}`),
        authFetch(`/analytics/lead-source?period=${period}`)
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data);
      }
      
      if (funnelRes.ok) {
        const data = await funnelRes.json();
        setFunnel(data.funnel || []);
      }
      
      if (trendRes.ok) {
        const data = await trendRes.json();
        setRevenueTrend(data.trend || []);
      }
      
      if (quoteRes.ok) {
        const data = await quoteRes.json();
        setQuotePerformance(data);
      }
      
      if (teamRes.ok) {
        const data = await teamRes.json();
        setTeamPerformance(data.performance || []);
      }
      
      if (leadSourceRes.ok) {
        const data = await leadSourceRes.json();
        setLeadSourceData(data);
      }
    } catch (err) {
      console.error('Analytics load error:', err);
      setLoadError(err.message || 'Failed to load analytics data');
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [authFetch, period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (loadError) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="analytics-error">
          <BarChart3 className="w-12 h-12 text-slate-300 mb-4" />
          <h2 className="text-lg font-semibold text-[#1e3a5f] mb-2">Unable to load analytics</h2>
          <p className="text-slate-500 mb-4">{loadError}</p>
          <Button onClick={loadAnalytics} className="rounded-xl">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Analytics
            </h1>
            <p className="text-slate-500 mt-1">
              Deep insights into your business performance
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 rounded-xl">
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="365d">Last year</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              onClick={loadAnalytics}
              className="rounded-xl"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Pipeline Value"
            value={formatCurrencyCompact(overview?.summary?.pipeline_value || 0)}
            subtitle="Active opportunities"
            icon={DollarSign}
            color="blue"
          />
          <StatCard
            title="Won Revenue"
            value={formatCurrencyCompact(overview?.summary?.won_value || 0)}
            subtitle={`${overview?.summary?.won_quotes || 0} deals closed`}
            icon={TrendingUp}
            color="emerald"
          />
          <StatCard
            title="Win Rate"
            value={`${overview?.conversion?.quote_win_rate || 0}%`}
            subtitle="Quote to won"
            icon={Target}
            color="violet"
          />
          <StatCard
            title="Avg Quote Value"
            value={formatCurrencyCompact(overview?.summary?.avg_quote_value || 0)}
            subtitle="Per quote"
            icon={BarChart3}
            color="amber"
          />
        </div>

        {/* Conversion Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-[#1e3a5f]">Enquiry → Quote</p>
                <p className="text-sm text-slate-500">Conversion rate</p>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-[#1e3a5f]">
                {overview?.conversion?.enquiry_to_quote || 0}%
              </span>
              <span className="text-sm text-slate-400 mb-1">
                {overview?.summary?.total_quotes || 0} / {overview?.summary?.total_enquiries || 0}
              </span>
            </div>
            <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${Math.min(overview?.conversion?.enquiry_to_quote || 0, 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-[#1e3a5f]">Win Rate</p>
                <p className="text-sm text-slate-500">Won vs Lost</p>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-emerald-600">
                {overview?.conversion?.quote_win_rate || 0}%
              </span>
              <span className="text-sm text-slate-400 mb-1">
                {overview?.summary?.won_quotes || 0} won / {overview?.summary?.lost_quotes || 0} lost
              </span>
            </div>
            <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.min(overview?.conversion?.quote_win_rate || 0, 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-[#1e3a5f]">New Contacts</p>
                <p className="text-sm text-slate-500">Pipeline growth</p>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-[#1e3a5f]">
                {overview?.summary?.total_contacts || 0}
              </span>
              <span className="text-sm text-slate-400 mb-1">
                contacts added
              </span>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline Funnel */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
              Pipeline Funnel
            </h3>
            {funnel.length === 0 || funnel.every(f => f.count === 0) ? (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No pipeline data yet</p>
                </div>
              </div>
            ) : (
              <ChartContainer height={280}>
                <BarChart
                  data={funnel}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis 
                    dataKey="stage" 
                    type="category" 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    width={70}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {funnel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </div>

          {/* Quote Status Distribution */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
              Quote Status Distribution
            </h3>
            {!quotePerformance.by_status || quotePerformance.by_status.length === 0 || quotePerformance.by_status.every(s => s.count === 0) ? (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <PieChart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No quotes yet</p>
                </div>
              </div>
            ) : (
              <ChartContainer height={280}>
                <RechartsPie>
                  <Pie
                    data={quotePerformance.by_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, count }) => `${status}: ${count}`}
                  >
                    {quotePerformance.by_status.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${value} quotes (${formatCurrencyCompact(props.payload.value)})`,
                      props.payload.status
                    ]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                </RechartsPie>
              </ChartContainer>
            )}
          </div>
        </div>

        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
            Revenue Trend
          </h3>
          <ChartContainer height={250}>
            <AreaChart
              data={revenueTrend}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={(date) => {
                  if (date.length === 7) return date.slice(5);
                  return date.slice(5);
                }}
              />
              <YAxis 
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={(v) => formatCurrencyCompact(v)}
              />
              <Tooltip 
                formatter={(value) => [formatCurrencyFull(value), 'Revenue']}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#10b981" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorValue)" 
              />
            </AreaChart>
          </ChartContainer>
        </div>

        {/* Discipline Performance */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
            Performance by Discipline
          </h3>
          <ChartContainer height={250}>
            <BarChart
              data={quotePerformance.by_discipline?.slice(0, 6) || []}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="discipline" 
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={(d) => d.charAt(0).toUpperCase() + d.slice(1)}
              />
              <YAxis 
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={(v) => formatCurrencyCompact(v)}
              />
              <Tooltip 
                formatter={(value, name) => [
                  formatCurrencyFull(value),
                  name === 'value' ? 'Total Value' : 'Won Value'
                ]}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="value" name="Total Value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="won_value" name="Won Value" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </div>

        {/* Team Performance */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
            Team Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Team Member</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Enquiries</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Quotes</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Outreach</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Tasks</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Won Value</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Score</th>
                </tr>
              </thead>
              <tbody>
                {teamPerformance.map((member, index) => (
                  <tr key={member.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                          index === 0 ? 'bg-amber-500' : 
                          index === 1 ? 'bg-slate-400' : 
                          index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                        }`}>
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{member.name}</p>
                          <p className="text-xs text-slate-400 capitalize">{member.role?.replace('_', ' ')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 font-medium text-sm">
                        {member.enquiries}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-medium text-sm">
                        {member.quotes}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 text-amber-700 font-medium text-sm">
                        {member.outreach}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 font-medium text-sm">
                        {member.tasks_completed}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-emerald-600">
                      {formatCurrencyCompact(member.won_value)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className="font-bold text-[#1e3a5f]">{member.score}</span>
                        {index === 0 && <Award className="w-4 h-4 text-amber-500" />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Lead Source Performance */}
      {leadSourceData && leadSourceData.by_profile && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                <Target className="w-5 h-5" />
                Lead Source Performance
              </h3>
              <p className="text-sm text-slate-500 mt-1">Revenue, conversion, and pipeline by scenario profile</p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-[#1e3a5f]">{leadSourceData.totals?.total_contacts || 0}</p>
              <p className="text-xs text-slate-500">Profiled Contacts</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-emerald-600">{formatCurrencyCompact(leadSourceData.totals?.total_revenue || 0)}</p>
              <p className="text-xs text-slate-500">Won Revenue</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-[#1e3a5f]">{leadSourceData.totals?.profiles_count || 0}</p>
              <p className="text-xs text-slate-500">Active Sources</p>
            </div>
          </div>

          {/* Profile breakdown table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-3 text-slate-500 font-medium">Source</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Contacts</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Quotes</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Won</th>
                  <th className="text-right py-3 px-3 text-slate-500 font-medium">Revenue</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Conv %</th>
                  <th className="text-right py-3 px-3 text-slate-500 font-medium hidden md:table-cell">Avg Pipeline</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium hidden md:table-cell">Outreach</th>
                </tr>
              </thead>
              <tbody>
                {leadSourceData.by_profile.map((item) => (
                  <tr key={item.profile.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.profile.color }} />
                        <span className="font-medium text-slate-800">{item.profile.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">W{item.profile.warmth_score}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center font-medium">{item.total_contacts}</td>
                    <td className="py-3 px-3 text-center">{item.total_quotes}</td>
                    <td className="py-3 px-3 text-center">
                      <span className={`font-semibold ${item.won_quotes > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {item.won_quotes}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right font-medium text-emerald-600">
                      {item.won_revenue > 0 ? formatCurrencyCompact(item.won_revenue) : '—'}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        item.conversion_rate >= 20 ? 'bg-emerald-100 text-emerald-700' :
                        item.conversion_rate > 0 ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {item.conversion_rate}%
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right text-slate-600 hidden md:table-cell">
                      {item.avg_pipeline_value > 0 ? formatCurrencyCompact(item.avg_pipeline_value) : '—'}
                    </td>
                    <td className="py-3 px-3 text-center text-slate-600 hidden md:table-cell">{item.outreach_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bar chart — Revenue by source */}
          {leadSourceData.by_profile.some(p => p.won_revenue > 0) && (
            <div className="mt-6">
              <p className="text-xs font-medium text-slate-500 mb-3">Won Revenue by Lead Source</p>
              <ChartContainer height={200}>
                <BarChart data={leadSourceData.by_profile.filter(p => p.total_contacts > 0).map(p => ({
                  name: p.profile.label.replace(' Re-engagement', '').replace('New ', ''),
                  revenue: p.won_revenue,
                  fill: p.profile.color
                }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrencyCompact(v)} />
                  <Tooltip formatter={(v) => formatCurrencyFull(v)} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                    {leadSourceData.by_profile.filter(p => p.total_contacts > 0).map((p, i) => (
                      <Cell key={i} fill={p.profile.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </div>
          )}
        </div>
      )}
    </MainLayout>
  );
};

export default AnalyticsPage;
